// Download a CSV template file with just the header row + a few blank rows
// for the user to fill in. Notes and examples are not included to keep the
// file clean and avoid confusing the parser. Help text in the UI explains
// what each column means.
export function downloadCsvTemplate(filename, columns) {
  const escape = (val) => {
    if (val == null) return '';
    const str = String(val);
    if (/[",\n\r]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const headerRow = columns.map(c => escape(c.label)).join(',');
  const blankRow = columns.map(() => '').join(',');

  // Header + 5 blank rows ready for data entry
  const csv = [headerRow, blankRow, blankRow, blankRow, blankRow, blankRow].join('\r\n');

  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Template column definitions for each module.
export const PROJECT_TEMPLATE_COLUMNS = [
  { key: 'name', label: 'Project Name', type: 'text', example: 'Website Redesign 2026', notes: 'Required' },
  { key: 'description', label: 'Description', type: 'text', example: 'Revamp marketing site', notes: 'Required' },
  { key: 'potentialId', label: 'Potential ID', type: 'text', example: '1234567', notes: 'Required — 7 digits (use a dummy SD potential for internal work)' },
  { key: 'billable', label: 'Billable', type: 'text', example: 'Yes', notes: 'Yes | No — blank means Yes' },
  { key: 'requester', label: 'Requester', type: 'text', example: 'jane.doe@flatworldsolutions.com', notes: 'Required for Marcom only' },
  { key: 'startDate', label: 'Start Date', type: 'date', example: '2026-04-01', notes: 'Optional — YYYY-MM-DD' },
  { key: 'endDate', label: 'End Date', type: 'date', example: '2026-06-30', notes: 'Optional — YYYY-MM-DD' },
  { key: 'billingMethod', label: 'Billing Method', type: 'text', example: 'hourly', notes: 'hourly | fixed | per_transaction' },
  { key: 'status', label: 'Status', type: 'text', example: 'planning', notes: 'planning | active | on_hold | completed | cancelled' },
  { key: 'estimatedHours', label: 'Estimated Hours', type: 'number', example: '120', notes: 'Optional — number' },
  { key: 'estimatedTransactions', label: 'Estimated Transactions', type: 'number', example: '0', notes: 'Optional — number' },
  { key: 'managerEmail', label: 'Project Manager Email', type: 'email', example: 'pm@flatworldsolutions.com', notes: 'Required' },
  { key: 'teamLeaderEmail', label: 'Team Leader Email', type: 'text', example: 'lead@flatworldsolutions.com', notes: 'Optional' },
  { key: 'teamMemberEmails', label: 'Team Member Emails', type: 'text', example: 'user1@fws.com; user2@fws.com', notes: 'Optional — separate with ;' },
];

export const TASK_TEMPLATE_COLUMNS = [
  { key: 'projectName', label: 'Project Name', type: 'text', example: 'Website Redesign 2026', notes: 'Required — must match existing project' },
  { key: 'title', label: 'Task Name', type: 'text', example: 'Build login page', notes: 'Required' },
  { key: 'description', label: 'Description', type: 'text', example: 'OAuth + email login UI', notes: 'Required' },
  { key: 'milestone', label: 'Milestone', type: 'text', example: 'Milestone 1', notes: 'Optional' },
  { key: 'userStory', label: 'User Story', type: 'text', example: 'US-101 Login', notes: 'Optional' },
  { key: 'sprint', label: 'Sprint', type: 'text', example: 'Sprint 3', notes: 'Optional' },
  { key: 'ownerEmail', label: 'Owner Email', type: 'email', example: 'owner@flatworldsolutions.com', notes: 'Required' },
  { key: 'assigneeEmails', label: 'Assignee Emails', type: 'text', example: 'dev1@fws.com; dev2@fws.com', notes: 'Required — separate with ;' },
  { key: 'status', label: 'Status', type: 'text', example: 'todo', notes: 'Optional — defaults to "todo"' },
  { key: 'priority', label: 'Priority', type: 'text', example: 'medium', notes: 'low | medium | high' },
  { key: 'estimatedHours', label: 'Estimated Hours', type: 'number', example: '8', notes: 'Optional — number' },
  { key: 'estimatedTransactions', label: 'Estimated Transactions', type: 'number', example: '0', notes: 'Optional — number' },
  { key: 'deadline', label: 'Deadline', type: 'date', example: '15/05/2026', notes: 'Optional — DD/MM/YYYY or MM/DD/YYYY' },
  { key: 'transactionType', label: 'Transaction Type', type: 'text', example: '', notes: 'Optional — service-specific' },
  { key: 'filelocation', label: 'File Location', type: 'text', example: '', notes: 'Marcom only' },
];

export const USER_TEMPLATE_COLUMNS = [
  { key: 'name', label: 'Name', type: 'text', example: 'Jane Doe', notes: 'Required' },
  { key: 'email', label: 'Email', type: 'email', example: 'jane.doe@flatworldsolutions.com', notes: 'Required — must be unique' },
  { key: 'password', label: 'Password', type: 'text', example: 'ChangeMe123!', notes: 'Required on creation' },
  { key: 'employeeId', label: 'Employee ID', type: 'text', example: 'FW1234', notes: 'Required — letters and numbers only, must be unique' },
  { key: 'role', label: 'Role', type: 'text', example: 'team_member', notes: 'admin | project_manager | team_leader | team_member | view_only | QC' },
  { key: 'serviceName', label: 'Service', type: 'text', example: 'Marcom', notes: 'Required — must match existing service name' },
  { key: 'reportsToEmail', label: 'Reports To Email', type: 'email', example: 'manager@flatworldsolutions.com', notes: 'Optional — email of an existing project manager / team leader who approves their timesheets' },
];

// Parse a CSV string into an array of row objects keyed by the column `key`
// values from `columns`. Skips header + notes rows (the top 2 rows in our
// generated templates), and any rows that are completely empty.
export function parseCsv(csvText, columns) {
  // Strip BOM if present
  const text = csvText.replace(/^﻿/, '');
  const lines = [];
  let cur = '';
  let inQuotes = false;
  // Tokenize line-by-line while respecting quoted newlines
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      if (inQuotes && text[i + 1] === '"') { cur += '"'; i++; }
      else { inQuotes = !inQuotes; }
    } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      lines.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  if (cur.length) lines.push(cur);

  const splitRow = (line) => {
    const out = [];
    let field = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') { field += '"'; i++; }
        else { inQ = !inQ; }
      } else if (ch === ',' && !inQ) {
        out.push(field);
        field = '';
      } else {
        field += ch;
      }
    }
    out.push(field);
    return out;
  };

  if (lines.length < 1) return [];

  const headerCells = splitRow(lines[0]).map(s => s.trim());
  // Map header label -> column.key
  const labelToKey = {};
  columns.forEach(col => { labelToKey[col.label.trim().toLowerCase()] = col.key; });

  // Determine which CSV column index maps to which key
  const indexToKey = headerCells.map(label => labelToKey[label.trim().toLowerCase()] || null);

  // Recover a row with too many cells (unquoted comma in a field) by trying
  // to merge consecutive cells at each text-type column position until the
  // result validates against the column type hints. First valid arrangement
  // wins; returns null if recovery isn't possible.
  const validators = {
    email: (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()),
    date: (v) => !v || /^\d{1,4}[-/]\d{1,4}[-/]\d{2,4}$/.test(v.trim()) || /^\d{4}-\d{2}-\d{2}$/.test(v.trim()),
    number: (v) => !v || /^-?\d+(\.\d+)?$/.test(v.trim()),
    text: () => true,
  };
  const tryRecover = (cells) => {
    const overflow = cells.length - headerCells.length;
    if (overflow <= 0) return null;
    // Need at least one strict column so we can verify the recovery worked
    const strictCol = columns.some((c) => c.type && c.type !== 'text');
    if (!strictCol) return null;
    for (let p = 0; p < headerCells.length; p++) {
      const key = indexToKey[p];
      const col = key ? columns.find((c) => c.key === key) : null;
      const t = (col && col.type) || 'text';
      if (t !== 'text') continue;
      const merged = [
        ...cells.slice(0, p),
        cells.slice(p, p + overflow + 1).join(',').trim(),
        ...cells.slice(p + overflow + 1),
      ];
      if (merged.length !== headerCells.length) continue;
      let ok = true;
      for (let i = 0; i < merged.length; i++) {
        const k = indexToKey[i];
        const c = k ? columns.find((cc) => cc.key === k) : null;
        const tt = (c && c.type) || 'text';
        if (!validators[tt](merged[i])) { ok = false; break; }
      }
      if (ok) return merged;
    }
    return null;
  };

  // Skip only the header (index 0). Everything else is treated as data.
  const dataRows = [];
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue; // skip empty lines
    let cells = splitRow(lines[i]);
    if (cells.every(c => !c.trim())) continue; // skip blank rows

    // Validate strict columns (email/date/number) against the cells as-is.
    // If anything fails, the row is probably misaligned — try recovery even
    // if cell count looks normal (Excel/Sheets often strip trailing empty
    // commas, which masks the overflow).
    const validateStrictCells = (rowCells) => {
      for (let p = 0; p < headerCells.length; p++) {
        if (p >= rowCells.length) return false;
        const k = indexToKey[p];
        const c = k ? columns.find((cc) => cc.key === k) : null;
        const tt = (c && c.type) || 'text';
        if (tt === 'text') continue;
        if (!validators[tt](rowCells[p])) return false;
      }
      return true;
    };

    // Auto-recover from unquoted commas: try merging consecutive cells at a
    // text-type column position so strict columns line up correctly.
    let autoFixed = false;
    if (cells.length > headerCells.length) {
      const recovered = tryRecover(cells);
      if (recovered) {
        cells = recovered;
        autoFixed = true;
      }
    } else if (cells.length === headerCells.length && !validateStrictCells(cells)) {
      // Excel/Sheets may have dropped one or more trailing empty cells.
      // Pad with empties and try recovery up to a few times.
      for (let pad = 1; pad <= 3; pad++) {
        const padded = [...cells];
        for (let n = 0; n < pad; n++) padded.push('');
        const recovered = tryRecover(padded);
        if (recovered) {
          cells = recovered;
          autoFixed = true;
          break;
        }
      }
    }

    const row = {};
    indexToKey.forEach((key, idx) => {
      if (key) row[key] = (cells[idx] || '').trim();
    });
    // If we couldn't auto-fix and there are still too many cells, flag it so
    // the row-level validator can show a clear "unquoted comma" error.
    if (cells.length > headerCells.length) {
      row._extraCells = cells.length - headerCells.length;
    }
    if (autoFixed) row._autoFixed = true;
    row._lineNumber = i + 1;
    dataRows.push(row);
  }
  return dataRows;
}

export const TIME_TRANSACTION_TEMPLATE_COLUMNS = [
  { key: 'employeeEmail', label: 'Employee Email', type: 'email', example: 'jane.doe@flatworldsolutions.com', notes: 'Required' },
  { key: 'projectName', label: 'Project Name', type: 'text', example: 'Website Redesign 2026', notes: 'Required — must match existing project' },
  { key: 'taskTitle', label: 'Task Title', type: 'text', example: 'Build login page', notes: 'Required — must match existing task' },
  { key: 'date', label: 'Date', type: 'date', example: '2026-04-23', notes: 'Required — YYYY-MM-DD (DD/MM/YYYY also accepted; ambiguous dates read day-first)' },
  { key: 'hours', label: 'Hours', type: 'number', example: '4.5', notes: 'Required — number' },
  { key: 'transactions', label: 'Transactions', type: 'number', example: '0', notes: 'Required (optional for FW AI SW and Corporate Software)' },
  { key: 'transactionType', label: 'Transaction Type', type: 'text', example: '', notes: 'Optional — service-specific' },
  { key: 'fileName', label: 'File Name / Description', type: 'text', example: 'Login spec v1', notes: 'Required (optional for FW AI SW and Corporate Software)' },
];
